import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { format, isValid } from 'date-fns';

import { Box, Button, FormField, FormHelp, Input, Label } from '@zillow/constellation-10';
import { IconPlusCircleFilled } from '@zillow/constellation-icons';

import { useQueryClient } from '@zg-rentals/react-query5';
import { PhoneVerificationSource } from '@zg-rentals/ts-rental-manager-api-client';
import { useRentalManagerUserPropertyEditV2WebApiHandler_phone } from '@zg-rentals/ts-rental-manager-api-client-react-query';

import { useScreenSize } from '../../../hooks';
import { getListingDetailsQueryKey, useGetListingDetails } from '../../../hooks/queries/properties';
import numberUtils from '../../../utils/numberUtils';
import propertyUtils from '../../../utils/propertyUtils';

import { DEFAULT_VERIFICATION_CONFIG } from '../../modals/VerificationModal/constants';

import { VerificationModal } from '../../modals/VerificationModal';

import PhoneNumberDisplayInfo from '../PhoneNumberDisplayInfo';

import type React from 'react';

type Props = {
  contactPhoneRef?: React.LegacyRef<HTMLDivElement>;
  isInputFormDisabled?: boolean;
};

const AddEditPhoneNumber = ({ contactPhoneRef, isInputFormDisabled = false }: Props) => {
  const router = useRouter();
  const { isScreenDesktop } = useScreenSize();
  const queryClient = useQueryClient();

  const listingAlias = router.query.listingAlias as string;

  const { data: listingDetails } = useGetListingDetails(listingAlias);

  const contactPhone = listingDetails?.phone || '';
  const contactPhoneDateVerified = listingDetails?.phoneVerifiedOnMs
    ? new Date(listingDetails.phoneVerifiedOnMs).toString()
    : '';

  const [verifiedPhoneNumber, setVerifiedPhoneNumber] = useState<string>(contactPhone || '');
  const [datePhoneVerified, setDatePhoneVerified] = useState('');
  const [showPhoneVerificationModal, setShowPhoneVerificationModal] = useState(false);

  const isRoomForRent = propertyUtils.isRoomForRent({ listing: listingDetails });

  const { mutateAsync: editPhoneNumber } = useRentalManagerUserPropertyEditV2WebApiHandler_phone({});

  useEffect(() => {
    if (contactPhone) {
      setVerifiedPhoneNumber(contactPhone);
      setDatePhoneVerified(contactPhoneDateVerified);
    }
  }, [contactPhone, contactPhoneDateVerified]);

  const updatePhoneNumber = async (phoneNumber: string) => {
    await editPhoneNumber({
      postData: {
        phone: phoneNumber,
        listingAlias,
      },
    }).then((response) => {
      setVerifiedPhoneNumber(response.contactPhone ?? phoneNumber);
      setDatePhoneVerified(response.verifiedMs ? new Date(response.verifiedMs).toString() : new Date().toString());
    });

    // Await refetch to ensure data is fresh before modal advances to success step
    await queryClient.refetchQueries({ queryKey: getListingDetailsQueryKey(listingAlias) });
  };

  return (
    <>
      {(verifiedPhoneNumber || !isInputFormDisabled) && (
        <>
          {contactPhoneRef && <div ref={contactPhoneRef} />}
          <FormField>
            <Label required={!isInputFormDisabled} disabled={isInputFormDisabled}>
              Phone number
            </Label>

            {verifiedPhoneNumber ? (
              <>
                <Box css={{ display: 'flex' }}>
                  <Input
                    disabled={true}
                    size={isScreenDesktop ? 'lg' : 'md'}
                    aria-required="true"
                    name="contactPhone"
                    type="tel"
                    value={numberUtils.formatPhoneNumberInput(verifiedPhoneNumber || '')}
                    aria-describedby={datePhoneVerified ? 'verification' : undefined}
                    className="fs-mask"
                    css={{
                      width: 'auto',
                      maxWidth: '300px',
                      marginRight: '400',
                      '&[disabled]': {
                        opacity: 1,
                      },
                    }}
                  />

                  {!isInputFormDisabled && (
                    <Button
                      size="sm"
                      tone="brand"
                      emphasis="outlined"
                      onClick={() => setShowPhoneVerificationModal(true)}
                    >
                      Edit
                    </Button>
                  )}
                </Box>

                <FormHelp id="verification">
                  {isValid(new Date(datePhoneVerified))
                    ? `Verified on ${format(new Date(datePhoneVerified), 'MMMM d, yyyy')}`
                    : 'Previously verified'}
                </FormHelp>
              </>
            ) : (
              <Button
                size="sm"
                tone="brand"
                emphasis="outlined"
                onClick={() => setShowPhoneVerificationModal(true)}
                icon={<IconPlusCircleFilled />}
              >
                Add phone number
              </Button>
            )}
          </FormField>
        </>
      )}

      {showPhoneVerificationModal && (
        <VerificationModal
          headerText="Add and verify phone number"
          initialContentToVerify={verifiedPhoneNumber}
          onContentValueAdded={updatePhoneNumber}
          onVerificationConfirmation={updatePhoneNumber}
          showVerificationModal={setShowPhoneVerificationModal}
          stepsConfig={DEFAULT_VERIFICATION_CONFIG}
          verificationSource={PhoneVerificationSource.LISTING_CONTACT}
          listingDetails={listingDetails}
        />
      )}

      {!isInputFormDisabled && verifiedPhoneNumber && !isRoomForRent && (
        <Box css={{ marginTop: '400' }}>
          <PhoneNumberDisplayInfo />
        </Box>
      )}
    </>
  );
};

export default AddEditPhoneNumber;
